import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

interface InitCreditsResult {
  success: boolean;
  balance: number;
  isNewUser: boolean;
}

interface GetCreditsResult {
  balance: number;
  totalPurchased: number;
  totalUsed: number;
}

interface ConsumeCreditsResult {
  success: boolean;
  balance: number;
}

interface CheckoutSessionResult {
  sessionId: string;
  url: string;
}

/**
 * Initialize credits for a new user - grants 5 free credits on first login
 */
export const initializeUserCredits = async (userId: string): Promise<InitCreditsResult> => {
  const initCredits = httpsCallable<{ userId: string }, InitCreditsResult>(
    functions,
    'initializeUserCredits'
  );
  const result = await initCredits({ userId });
  return result.data;
};

/**
 * Get user's current credit balance
 */
export const getUserCredits = async (userId: string): Promise<GetCreditsResult> => {
  const getCredits = httpsCallable<{ userId: string }, GetCreditsResult>(
    functions,
    'getUserCredits'
  );
  const result = await getCredits({ userId });
  return result.data;
};

/**
 * Consume 1 credit for a search action
 */
export const consumeCredit = async (userId: string, query?: string): Promise<ConsumeCreditsResult> => {
  const consume = httpsCallable<{ userId: string; query?: string }, ConsumeCreditsResult>(
    functions,
    'consumeCredit'
  );
  const result = await consume({ userId, query });
  return result.data;
};

/**
 * Create a Stripe checkout session for purchasing credits
 * @param userId - User's Firebase UID
 * @param email - User's email
 * @param amount - Amount in dollars (min $1, max $1000)
 */
export const createCreditCheckoutSession = async (
  userId: string,
  email: string,
  amount: number
): Promise<CheckoutSessionResult> => {
  const createSession = httpsCallable<
    { userId: string; email: string; amount: number },
    CheckoutSessionResult
  >(functions, 'createCreditCheckoutSession');
  const result = await createSession({ userId, email, amount });
  return result.data;
};

interface AdRewardResult {
  success: boolean;
  balance: number;
}

/**
 * Grant 1 credit for watching a rewarded ad
 */
export const grantAdRewardCredit = async (userId: string): Promise<AdRewardResult> => {
  const grantReward = httpsCallable<{ userId: string }, AdRewardResult>(
    functions,
    'grantAdRewardCredit'
  );
  const result = await grantReward({ userId });
  return result.data;
};

interface AdminGrantResult {
  success: boolean;
  balance: number;
}

/**
 * Admin-only: grant credits without payment
 */
export const adminGrantCredits = async (userId: string, email: string, amount: number): Promise<AdminGrantResult> => {
  const grant = httpsCallable<{ userId: string; email: string; amount: number }, AdminGrantResult>(
    functions,
    'adminGrantCredits'
  );
  const result = await grant({ userId, email, amount });
  return result.data;
};

// Credit pricing constants (should match backend)
export const CREDITS_PER_DOLLAR = 20;
export const MIN_PURCHASE_AMOUNT = 1; // $1 minimum
export const MAX_PURCHASE_AMOUNT = 1000; // $1000 maximum

